import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api.js';
import { groupByBooking, useCollapsedGroups } from '../../utils/bookingGroups.js';

function fmt(ts) {
  return ts ? new Date(ts).toLocaleString() : '—';
}

// Convert an ISO timestamp to the value a datetime-local input expects, keeping
// the seconds so an untouched or corrected value is not truncated to the minute.
function toLocalInput(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

export default function ClockCorrectionsPage() {
  const [jobs, setJobs] = useState([]);
  // 8990: jobs are listed beneath their booking; each booking folds away.
  const [isCollapsed, toggleGroup] = useCollapsedGroups();
  const [selected, setSelected] = useState(null);
  const [timesheet, setTimesheet] = useState(null);
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [pageMsg, setPageMsg] = useState(null);

  const [correction, setCorrection] = useState(null); // { kind, id, label, current }
  const [newTimestamp, setNewTimestamp] = useState('');
  const [reason, setReason] = useState('');
  const [correctionError, setCorrectionError] = useState('');
  const [saving, setSaving] = useState(false);

  const loadJobs = useCallback(async () => {
    const res = await api.listBillingJobs();
    setJobs(res || []);
    return res || [];
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        await loadJobs();
      } catch (err) {
        setLoadError(err.message || 'Failed to load billing jobs.');
      } finally {
        setLoading(false);
      }
    })();
  }, [loadJobs]);

  const selectJob = async (job) => {
    setPageMsg(null);
    setSelected(job);
    const [ts, ledger] = await Promise.all([api.getTimesheet(job.id), api.getAuditLedger(job.id)]);
    setTimesheet(ts);
    setAudits(ledger || []);
  };

  const refresh = async () => {
    if (!selected) return;
    const [ts, ledger] = await Promise.all([api.getTimesheet(selected.id), api.getAuditLedger(selected.id)]);
    setTimesheet(ts);
    setAudits(ledger || []);
  };

  const initiate = (kind, row) => {
    setCorrectionError('');
    setCorrection({ kind, id: row.id, label: row.label, current: row.logged_at });
    setNewTimestamp(toLocalInput(row.logged_at));
    setReason('');
  };

  const save = async (e) => {
    e.preventDefault();
    setCorrectionError('');
    if (!newTimestamp) {
      setCorrectionError('Enter the corrected timestamp.');
      return;
    }
    if (!reason.trim()) {
      setCorrectionError('A correction justification (audit reason) is required.');
      return;
    }
    setSaving(true);
    try {
      const payload = { newLoggedAt: new Date(newTimestamp).toISOString(), reason: reason.trim() };
      if (correction.kind === 'clock') await api.adjustClock(correction.id, payload);
      else await api.adjustMilestone(correction.id, payload);
      setPageMsg({ type: 'success', text: 'Timestamp corrected — original value archived to the audit ledger.' });
      setCorrection(null);
      await refresh();
    } catch (err) {
      setCorrectionError(err.message || 'Failed to save correction.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container-fluid py-4" data-testid="corrections-page">
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center bg-white p-3 rounded shadow-sm">
            <div>
              <h3 className="text-primary mb-0">
                <i className="fas fa-clock-rotate-left me-2" aria-hidden="true" />
                Execution Clock Corrections
              </h3>
              <p className="text-muted mb-0 small">
                Audited adjustment of logged clocks &amp; milestones before billing.
              </p>
            </div>
            <span className="badge bg-primary p-2">Audited</span>
          </div>
        </div>
      </div>

      {loadError && (
        <div className="alert alert-danger" data-testid="corrections-load-error">
          {loadError}
        </div>
      )}
      {pageMsg && (
        <div className={`alert alert-${pageMsg.type}`} role="alert" data-testid="correction-message">
          {pageMsg.text}
        </div>
      )}

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
        </div>
      ) : (
        <div className="row">
          <div className="col-lg-4 mb-4">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white pt-4 border-bottom-0">
                <h5 className="mb-0 text-secondary">
                  <i className="fas fa-list me-2" aria-hidden="true" />
                  Jobs with Logged Time
                </h5>
              </div>
              <div className="list-group list-group-flush">
                {jobs.length === 0 && <div className="list-group-item text-muted small">No jobs with logs.</div>}
                {groupByBooking(jobs).map((g) => {
                  const collapsed = isCollapsed(g.key);
                  return (
                    <React.Fragment key={g.key}>
                      <button
                        type="button"
                        className="list-group-item list-group-item-light d-flex align-items-center gap-2"
                        onClick={() => toggleGroup(g.key)}
                        aria-expanded={!collapsed}
                        data-testid="booking-group"
                        data-ref={g.ref}
                      >
                        <i className={`fas fa-chevron-${collapsed ? 'right' : 'down'} small`} aria-hidden="true" />
                        <span className="text-start">
                          <span className="fw-bold d-block">{g.ref}</span>
                          <small className="text-muted" data-testid="booking-group-detail">
                            {[g.first.vessel_name, g.first.service_name].filter(Boolean).join(' · ')}
                          </small>
                        </span>
                        <span className="badge bg-secondary-subtle text-secondary-emphasis border ms-auto" data-testid="booking-group-count">
                          {g.rows.length} job{g.rows.length === 1 ? '' : 's'}
                        </span>
                      </button>
                      {!collapsed &&
                        g.rows.map((j) => (
                          <button
                            key={j.id}
                            type="button"
                            className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ps-5${
                              selected?.id === j.id ? ' active' : ''
                            }`}
                            onClick={() => selectJob(j)}
                            data-testid="job-row"
                            data-ref={j.booking_ref}
                            data-job-no={j.job_no || undefined}
                          >
                            <span className="text-start">
                              <span className="d-block" data-testid="job-row-name">
                                {j.job_no ? `${j.job_no} · ${j.job_name}` : `Ticket No. ${j.ticket_no ?? '—'}`}
                              </span>
                              <small className={selected?.id === j.id ? '' : 'text-muted'}>
                                {j.tug_name || 'No tug'}
                                {j.ticket_no != null && j.job_no ? ` · Ticket No. ${j.ticket_no}` : ''}
                              </small>
                            </span>
                            <span className="badge bg-light text-dark border">{j.status}</span>
                          </button>
                        ))}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="col-lg-8 mb-4">
            {!selected ? (
              <div className="alert alert-secondary">Select a job to review its logged timestamps.</div>
            ) : (
              <>
                <div className="card border-0 shadow-sm mb-4">
                  <div className="card-header bg-white pt-4 border-bottom-0">
                    <h5 className="mb-0 text-secondary">
                      <i className="fas fa-stopwatch me-2" aria-hidden="true" />
                      Logged Timestamps — {selected.booking_ref}
                      {selected.job_no ? ` · ${selected.job_no}` : ''}
                      {selected.tug_name ? ` (${selected.tug_name})` : ''}
                    </h5>
                  </div>
                  <div className="card-body p-0">
                    <div className="table-responsive">
                      <table className="table table-hover align-middle mb-0">
                        <thead className="bg-light text-secondary small text-uppercase">
                          <tr>
                            <th>Entry</th>
                            <th>Logged At</th>
                            <th aria-label="action" />
                          </tr>
                        </thead>
                        <tbody>
                          {(timesheet?.clockEvents || []).map((c) => (
                            <tr key={c.id} data-testid="timesheet-row" data-label={c.label}>
                              <td className="fw-bold">{c.label}</td>
                              <td>{fmt(c.logged_at)}</td>
                              <td className="text-end">
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => initiate('clock', c)}
                                  data-testid="initiate-correction"
                                >
                                  <i className="fas fa-pen me-1" aria-hidden="true" />
                                  Initiate Correction
                                </button>
                              </td>
                            </tr>
                          ))}
                          {(timesheet?.milestones || []).map((m) => (
                            <tr key={m.id} data-testid="timesheet-row" data-label={m.label}>
                              <td>{m.label}</td>
                              <td>{fmt(m.logged_at)}</td>
                              <td className="text-end">
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => initiate('milestone', m)}
                                  data-testid="initiate-correction"
                                >
                                  <i className="fas fa-pen me-1" aria-hidden="true" />
                                  Initiate Correction
                                </button>
                              </td>
                            </tr>
                          ))}
                          {(timesheet?.clockEvents || []).length === 0 &&
                            (timesheet?.milestones || []).length === 0 && (
                              <tr>
                                <td colSpan={3} className="text-center text-muted py-4">
                                  No logged timestamps.
                                </td>
                              </tr>
                            )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <div className="card border-0 shadow-sm" data-testid="audit-ledger">
                  <div className="card-header bg-white pt-4 border-bottom-0">
                    <h5 className="mb-0 text-secondary">
                      <i className="fas fa-clipboard-list me-2" aria-hidden="true" />
                      Audit Ledger
                    </h5>
                  </div>
                  <div className="card-body p-0">
                    <div className="table-responsive">
                      <table className="table table-sm align-middle mb-0">
                        <thead className="bg-light text-secondary small text-uppercase">
                          <tr>
                            <th>Entry</th>
                            <th>Original</th>
                            <th>New</th>
                            <th>Reason</th>
                            <th>By</th>
                          </tr>
                        </thead>
                        <tbody>
                          {audits.length === 0 && (
                            <tr>
                              <td colSpan={5} className="text-center text-muted py-3">
                                No corrections recorded.
                              </td>
                            </tr>
                          )}
                          {audits.map((a) => (
                            <tr key={a.id} data-testid="audit-row">
                              <td className="fw-bold">{a.label}</td>
                              <td className="small">{fmt(a.old_value)}</td>
                              <td className="small">{fmt(a.new_value)}</td>
                              <td className="small">{a.reason}</td>
                              <td className="small">{a.adjusted_by || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Correction modal */}
      {correction && (
        <>
          <div className="modal d-block" tabIndex={-1} role="dialog" data-testid="correction-modal">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <form onSubmit={save} noValidate>
                  <div className="modal-header">
                    <h5 className="modal-title">
                      <i className="fas fa-pen me-2 text-primary" aria-hidden="true" />
                      Correct “{correction.label}”
                    </h5>
                    <button type="button" className="btn-close" aria-label="Close" onClick={() => setCorrection(null)} />
                  </div>
                  <div className="modal-body">
                    {correctionError && (
                      <div className="alert alert-danger" role="alert" data-testid="correction-error">
                        {correctionError}
                      </div>
                    )}
                    <p className="small text-muted">
                      Current value: <strong>{fmt(correction.current)}</strong>
                    </p>
                    <div className="mb-3">
                      <label htmlFor="new-ts" className="form-label small fw-bold text-muted">
                        Corrected Timestamp <span className="text-danger">*</span>
                      </label>
                      <input
                        id="new-ts"
                        type="datetime-local"
                        step="1"
                        className="form-control form-control-sm"
                        value={newTimestamp}
                        onChange={(e) => setNewTimestamp(e.target.value)}
                        data-testid="new-timestamp"
                      />
                    </div>
                    <div className="mb-1">
                      <label htmlFor="c-reason" className="form-label small fw-bold text-muted">
                        Correction Justification (audit reason) <span className="text-danger">*</span>
                      </label>
                      <textarea
                        id="c-reason"
                        rows={2}
                        className="form-control form-control-sm"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        data-testid="correction-reason"
                      />
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setCorrection(null)}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary btn-sm" disabled={saving} data-testid="save-correction">
                      {saving && (
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      )}
                      <i className="fas fa-save me-1" aria-hidden="true" />
                      Save Changes
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
    </div>
  );
}
